import { useState, useCallback, useEffect } from 'react';
import { Head, usePage } from '@inertiajs/react';
import { MainLayout } from '../../layouts/MainLayout';
import { getCSRFToken } from '../../utils/csrf';
import { route } from '../../utils/route';
import { assetUrl } from '../../utils/assetUrl';
import { useGeocoding } from '../../hooks/useGeocoding';
import { useGps } from '../../hooks/useGps';
import { AddressTypeSelector } from '../../components/delivery/AddressTypeSelector';
import { StreetInput } from '../../components/delivery/StreetInput';
import { NeighborhoodSelect } from '../../components/delivery/NeighborhoodSelect';
import type { AddressType, GeocodedAddress } from '../../types/address';
import {
  trackGiftStepViewed,
  trackGiftPackToggled,
  trackGiftStepCompleted,
  trackGiftStepError,
  trackGiftConfirmed,
} from '../../utils/tracking';

interface Pack {
  id: number;
  title: string;
  slug: string | null;
  description: string | null;
  calculated_price: number;
  image_url: string | null;
  items: Array<{
    product_id: number;
    product_title: string | null;
    product_format_id: number;
    format_name: string | null;
    quantity: number;
    product_image_url: string | null;
  }>;
}

interface SpecialDate {
  name: string;
  suggestions: string[];
}

interface GiftProps {
  packs: Pack[];
  specialDates: SpecialDate[];
  defaultSuggestions: string[];
}

type WizardStep = 'intro' | 'recipient' | 'from' | 'message' | 'delivery' | 'preview';

const STEPS: WizardStep[] = ['intro', 'recipient', 'from', 'message', 'delivery', 'preview'];

function getDisplayName(location: GeocodedAddress, data: { block?: string; house?: string; suite?: string; isCountry?: boolean }) {
  if (data.isCountry) {
    const city = location.address?.city || 'Cordoba';
    const name = location.display_name;
    if (!data.block || !data.house) return `${name}, ${city}, Argentina`;
    return `${name} Mzna. ${data.block} Lote ${data.house}, ${city}, Argentina`;
  }
  const road = location.address?.road || location.address?.pedestrian || '';
  const number = location.address?.house_number || 'S/N';
  const suite = data.suite ? ` - ${data.suite}` : '';
  const city = location.address?.city || 'Cordoba';
  return `${road} ${number}${suite}, ${city}, Argentina`;
}

export default function GiftWizard({ packs, specialDates, defaultSuggestions }: GiftProps) {
  const { props } = usePage<{ auth?: { user?: { name?: string; lastname?: string } }; cdnUrl?: string }>();
  const userName = props.auth?.user ? `${props.auth.user.name || ''} ${props.auth.user.lastname || ''}`.trim() : '';
  const cdnUrl = props.cdnUrl || '';
  const img = (path: string) => assetUrl(cdnUrl, path);

  const [step, setStep] = useState<WizardStep>('intro');
  const [selectedPackIds, setSelectedPackIds] = useState<number[]>([]);

  // Recipient data
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');

  // Address data
  const [addressType, setAddressType] = useState<AddressType>('street');
  const [streetAddress, setStreetAddress] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [geocodedAddress, setGeocodedAddress] = useState<GeocodedAddress | null>(null);
  const [streetSuggestions, setStreetSuggestions] = useState<GeocodedAddress[]>([]);
  const [neighborhoodSuggestions, setNeighborhoodSuggestions] = useState<GeocodedAddress[]>([]);
  const [block, setBlock] = useState('');
  const [house, setHouse] = useState('');
  const [suite, setSuite] = useState('');
  const [notes, setNotes] = useState('');

  // From / message
  const [fromName, setFromName] = useState('');
  const [message, setMessage] = useState('');

  // Delivery
  const [deliveryDates, setDeliveryDates] = useState<Array<{ value: string; label: string }>>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isCountry = addressType === 'country';

  const { searchAddress, searchNeighborhood, reverseGeocode, isLoading: isGeocodingLoading, error: geocodingError } =
    useGeocoding({ debounceMs: 300 });
  const { getCurrentPosition, isLoading: isGpsLoading, error: gpsError } = useGps();

  useEffect(() => {
    trackGiftStepViewed(step, selectedPackIds.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Search on input change (like AddressForm)
  useEffect(() => {
    if (!isCountry && streetAddress.length >= 3) {
      searchAddress(streetAddress).then(setStreetSuggestions);
    } else {
      setStreetSuggestions([]);
    }
  }, [streetAddress, isCountry, searchAddress]);

  useEffect(() => {
    if (isCountry && neighborhood.length >= 2) {
      searchNeighborhood(neighborhood).then(setNeighborhoodSuggestions);
    } else {
      setNeighborhoodSuggestions([]);
    }
  }, [neighborhood, isCountry, searchNeighborhood]);

  const handleStreetAddressSelect = useCallback((address: GeocodedAddress | null) => {
    if (address?.is_private_neighborhood && address.parsed_country_data) {
      setAddressType('country');
      setNeighborhood(address.parsed_country_data.neighborhood);
      setBlock(address.parsed_country_data.block || '');
      setHouse(address.parsed_country_data.house || '');
      setStreetAddress('');
      setGeocodedAddress(address);
      setStreetSuggestions([]);
      setNeighborhoodSuggestions([]);
    } else {
      setGeocodedAddress(address);
      if (address) setStreetAddress(address.display_name);
      setStreetSuggestions([]);
    }
  }, []);

  const handleNeighborhoodSelect = useCallback((address: GeocodedAddress | null) => {
    setGeocodedAddress(address);
    if (address) setNeighborhood(address.display_name);
    setNeighborhoodSuggestions([]);
  }, []);

  const handleTypeChange = (type: AddressType) => {
    setAddressType(type);
    setStreetAddress(''); setNeighborhood(''); setBlock(''); setHouse('');
    setGeocodedAddress(null); setStreetSuggestions([]); setNeighborhoodSuggestions([]);
  };

  const handleUseGps = async () => {
    try {
      const position = await getCurrentPosition();
      const result = await reverseGeocode(position.latitude, position.longitude);
      if (result) {
        if (result.private_neighborhood) {
          setAddressType('country');
          setGeocodedAddress({ ...result.private_neighborhood, lat: position.latitude, lon: position.longitude });
          setNeighborhood(result.private_neighborhood.display_name);
        } else {
          setAddressType('street');
          setGeocodedAddress(result);
          setStreetAddress(result.display_name);
        }
      }
    } catch {}
  };

  const togglePack = (packId: number) => {
    setSelectedPackIds(prev => {
      const isAdding = !prev.includes(packId);
      const next = isAdding ? [...prev, packId] : prev.filter(id => id !== packId);
      trackGiftPackToggled(isAdding ? 'add' : 'remove', packId, next.length);
      return next;
    });
  };

  const currentStepIndex = STEPS.indexOf(step);
  const goBack = () => {
    if (currentStepIndex > 0) {
      setStep(STEPS[currentStepIndex - 1]);
      setError(null);
    }
  };

  const validateAddress = async () => {
    if (!geocodedAddress) {
      setError('Ingresá una dirección válida.');
      trackGiftStepError('recipient', 'address_missing');
      return false;
    }
    if (isCountry && (!block || !house)) {
      setError('Ingresá la manzana y el lote.');
      trackGiftStepError('recipient', 'block_or_house_missing');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(route('gift.validate-address'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': getCSRFToken(),
        },
        body: JSON.stringify({
          address: getDisplayName(geocodedAddress, { block, house, suite, isCountry }),
          lat: geocodedAddress.lat,
          lng: geocodedAddress.lon,
        }),
      });
      const data = await response.json();
      if (!data.success) {
        setError(data.message);
        trackGiftStepError('recipient', 'address_out_of_zone');
        return false;
      }
      setDeliveryDates(data.dates);
      return true;
    } catch {
      setError('Error al validar la dirección.');
      trackGiftStepError('recipient', 'address_validation_network_error');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = async () => {
    setError(null);

    const totalValue = selectedPackIds.reduce((sum, id) => {
      const pack = packs.find(p => p.id === id);
      return sum + (pack?.calculated_price ?? 0);
    }, 0);

    if (step === 'intro') {
      if (selectedPackIds.length === 0) {
        setError('Elegí al menos una selección para regalar.');
        trackGiftStepError('intro', 'no_packs_selected');
        return;
      }
      trackGiftStepCompleted('intro', selectedPackIds.length, totalValue);
      setStep('recipient');
    } else if (step === 'recipient') {
      if (!recipientName.trim()) { setError('Ingresá el nombre del homenajeado.'); trackGiftStepError('recipient', 'recipient_name_missing'); return; }
      if (!recipientPhone.trim()) { setError('Ingresá el teléfono del homenajeado.'); trackGiftStepError('recipient', 'recipient_phone_missing'); return; }
      if (!geocodedAddress) { setError('Ingresá la dirección de entrega.'); trackGiftStepError('recipient', 'address_missing'); return; }
      const valid = await validateAddress();
      if (valid) {
        trackGiftStepCompleted('recipient', selectedPackIds.length, totalValue);
        setStep('from');
      }
    } else if (step === 'from') {
      if (!fromName.trim()) { setError('Ingresá de parte de quién es el regalo.'); trackGiftStepError('from', 'from_name_missing'); return; }
      trackGiftStepCompleted('from', selectedPackIds.length, totalValue);
      setStep('message');
    } else if (step === 'message') {
      trackGiftStepCompleted('message', selectedPackIds.length, totalValue);
      setStep('delivery');
    } else if (step === 'delivery') {
      if (!selectedDate) { setError('Elegí una fecha de entrega.'); trackGiftStepError('delivery', 'date_missing'); return; }
      trackGiftStepCompleted('delivery', selectedPackIds.length, totalValue);
      setStep('preview');
    }
  };

  const handleConfirm = async () => {
    if (!geocodedAddress || !selectedDate) return;

    setIsLoading(true);
    setError(null);

    const addressStr = getDisplayName(geocodedAddress, { block, house, suite, isCountry });

    try {
      // First save session
      const saveRes = await fetch(route('gift.save-session'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-XSRF-TOKEN': getCSRFToken() },
        body: JSON.stringify({
          recipient_name: recipientName,
          recipient_phone: recipientPhone,
          address: addressStr,
          lat: geocodedAddress.lat,
          lng: geocodedAddress.lon,
          suite: suite || null,
          notes: notes || null,
          neighborhood: isCountry ? geocodedAddress.display_name : '',
          is_country: isCountry,
          block: block || null,
          house: house || null,
          from: fromName,
          message: message || null,
          delivery_date: selectedDate,
          pack_ids: selectedPackIds,
        }),
      });
      const saveData = await saveRes.json();
      if (!saveData.success) {
        setError(saveData.message);
        trackGiftStepError('preview', 'save_session_failed');
        return;
      }

      trackGiftConfirmed(selectedPackIds.length, totalGiftPrice);
      // Redirect to standard checkout — cart is populated server-side from pack_ids
      window.location.href = route('orders.confirm');
    } catch {
      setError('Error de conexión. Intentá de nuevo.');
      trackGiftStepError('preview', 'network_error');
    } finally {
      setIsLoading(false);
    }
  };

  const allSuggestions = [
    ...specialDates.flatMap(d => d.suggestions),
    ...defaultSuggestions,
  ];
  // Deduplicate
  const messageSuggestions = [...new Set(allSuggestions)];

  const fromSuggestions = [
    ...(userName ? [userName] : []),
    'Tu amigo/a',
    'Tu familia',
    'Con cariño',
  ];

  const selectedDateLabel = deliveryDates.find(d => d.value === selectedDate)?.label || '';

  const totalGiftPrice = selectedPackIds.reduce((sum, id) => {
    const pack = packs.find(p => p.id === id);
    return sum + (pack?.calculated_price ?? 0);
  }, 0);

  return (
    <MainLayout>
      <Head title="Regalá frescura - Frutas y verduras frescas a domicilio | Abasto Virtual">
        <meta
          name="description"
          content="Regalá frescura: packs de frutas y verduras de estación con entrega a domicilio en Córdoba. Personalizá el mensaje y elegí el día de entrega."
        />
        <link rel="canonical" href="https://www.abastovirtual.com/regalar" />
        <meta property="og:title" content="Regalá frescura - Abasto Virtual" />
        <meta
          property="og:description"
          content="Sorprendé con un regalo de frutas y verduras frescas. Personalizá el mensaje y elegí el día de entrega."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.abastovirtual.com/regalar" />
      </Head>

      {/* Progress bar — hidden on intro */}
      {step !== 'intro' && (
        <div className="max-w-2xl mx-auto px-4 pt-6">
          <div className="flex gap-1">
            {STEPS.filter(s => s !== 'intro').map((s, i) => (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
                  i <= currentStepIndex - 1 ? 'bg-green-500' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>
      )}

      {/* ══════════ INTRO / LANDING ══════════ */}
      {step === 'intro' && (
        <>
          {/* ── Hero ── */}
          <section className="relative overflow-hidden bg-gradient-to-b from-[#f0f7ee] via-[#f6faf4] to-white">
            {/* Decorative blobs */}
            <div className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full bg-green-200/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-amber-100/40 blur-3xl" />

            <div className="relative mx-auto max-w-6xl px-4 pb-16 pt-12 sm:pt-20">
              <div className="flex flex-col items-center gap-8 md:flex-row md:gap-12 lg:gap-20">
                {/* Copy */}
                <div className="flex-1 text-center md:text-left">
                  <span className="mb-4 inline-block rounded-full border border-green-200 bg-white/80 px-4 py-1.5 text-sm font-medium text-green-700 shadow-sm backdrop-blur">
                    Nuevo en Abasto Virtual
                  </span>
                  <h1 className="mt-3 text-[2.5rem] font-extrabold leading-[1.1] tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                    Regalá{' '}
                    <span className="relative">
                      <span className="relative z-10">frescura</span>
                      <span className="absolute bottom-1 left-0 -z-0 h-3 w-full rounded bg-green-200/60 sm:h-4" />
                    </span>
                  </h1>
                  <p className="mt-5 text-lg leading-relaxed text-gray-600 sm:text-xl md:max-w-md">
                    Sorprendé a alguien especial con una selección de frutas y verduras frescas, directo a su puerta.
                    Nosotros nos encargamos de todo.
                  </p>
                  <a
                    href="#gift-packs"
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.getElementById('gift-packs');
                      if (el) {
                        const y = el.getBoundingClientRect().top + window.scrollY - 20;
                        window.scrollTo({ top: y, behavior: 'smooth' });
                      }
                    }}
                    className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-green-600 px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-green-600/25 transition-all hover:bg-green-700 hover:shadow-xl hover:shadow-green-600/30 active:scale-[0.98]"
                  >
                    Elegí qué regalar
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
                  </a>
                </div>

                {/* Hero image */}
                <div className="relative flex-shrink-0">
                  <div className="relative h-72 w-72 sm:h-80 sm:w-80 lg:h-[22rem] lg:w-[22rem]">
                    <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-green-100 to-amber-50 rotate-3" />
                    <img
                      src={img('/img/gift/hero-box.webp')}
                      alt="Canasta de regalo con frutas y verduras frescas"
                      className="relative z-10 h-full w-full rounded-[2rem] object-cover shadow-2xl shadow-green-900/10 -rotate-1"
                    />
                    <div className="absolute -bottom-3 -right-3 z-20 flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-3xl shadow-lg">
                      🎁
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── Cómo funciona ── */}
          <section className="bg-white py-16 sm:py-20">
            <div className="mx-auto max-w-5xl px-4">
              <h2 className="mb-4 text-center text-sm font-semibold uppercase tracking-widest text-green-600">
                Cómo funciona
              </h2>
              <p className="mx-auto mb-14 max-w-lg text-center text-2xl font-bold text-gray-900 sm:text-3xl">
                Tres pasos y listo
              </p>

              <div className="grid gap-10 sm:grid-cols-3 sm:gap-6 lg:gap-12">
                {[
                  {
                    num: '01',
                    title: 'Elegí',
                    desc: 'Seleccioná uno o varios packs con lo mejor de la temporada.',
                    img: img('/img/gift/hero-box.webp'),
                    color: 'from-green-50 to-emerald-50',
                    accent: 'bg-green-600',
                  },
                  {
                    num: '02',
                    title: 'Personalizá',
                    desc: 'Agregá un mensaje personal. Le avisamos por WhatsApp cuando esté en camino.',
                    img: img('/img/gift/whatsapp-preview.webp'),
                    color: 'from-amber-50 to-orange-50',
                    accent: 'bg-amber-500',
                  },
                  {
                    num: '03',
                    title: 'Entregamos',
                    desc: 'Llevamos el regalo a su puerta en el día y horario que elijas.',
                    img: img('/img/gift/delivery.webp'),
                    color: 'from-rose-50 to-pink-50',
                    accent: 'bg-rose-500',
                  },
                ].map((s) => (
                  <div key={s.num} className="group flex flex-col items-center text-center">
                    <div className={`relative mb-6 h-48 w-48 overflow-hidden rounded-3xl bg-gradient-to-br ${s.color} sm:h-40 sm:w-40 lg:h-52 lg:w-52`}>
                      <img
                        src={s.img}
                        alt={s.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <span className={`absolute left-3 top-3 flex h-8 w-8 items-center justify-center rounded-lg text-xs font-bold text-white ${s.accent}`}>
                        {s.num}
                      </span>
                    </div>
                    <h3 className="mb-2 text-lg font-bold text-gray-900">{s.title}</h3>
                    <p className="text-sm leading-relaxed text-gray-500">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── Pack selection ── */}
          <section id="gift-packs" className="scroll-mt-8 bg-gradient-to-b from-white via-[#fafdf9] to-[#f0f7ee] pb-20 pt-12 sm:pt-16">
            <div className="mx-auto max-w-2xl px-4">
              <h2 className="mb-2 text-center text-2xl font-bold text-gray-900 sm:text-3xl">
                Elegí qué regalar
              </h2>
              <p className="mb-10 text-center text-gray-500">
                Seleccioná uno o más packs. Podés combinar.
              </p>

              <div className="space-y-3">
                {packs.map(pack => {
                  const isSelected = selectedPackIds.includes(pack.id);
                  return (
                    <button
                      key={pack.id}
                      onClick={() => togglePack(pack.id)}
                      className={`group w-full text-left rounded-2xl border-2 p-4 transition-all duration-200 ${
                        isSelected
                          ? 'border-green-500 bg-green-50/80 shadow-md shadow-green-500/10'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        {pack.image_url && (
                          <img
                            src={pack.image_url}
                            alt={pack.title}
                            className="h-[4.5rem] w-[4.5rem] flex-shrink-0 rounded-xl object-cover"
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-semibold text-gray-900">{pack.title}</h3>
                            <span className="flex-shrink-0 rounded-lg bg-green-100 px-2.5 py-1 text-sm font-bold text-green-800">
                              ${pack.calculated_price.toLocaleString('es-AR')}
                            </span>
                          </div>
                          {pack.description && (
                            <p className="mt-1 text-sm leading-snug text-gray-500 line-clamp-2">{pack.description}</p>
                          )}
                          <p className="mt-1.5 text-xs text-gray-400">
                            {pack.items.length} productos frescos
                          </p>
                        </div>
                        <div className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                          isSelected ? 'border-green-500 bg-green-500' : 'border-gray-300 group-hover:border-gray-400'
                        }`}>
                          {isSelected && (
                            <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Sticky bottom bar when packs are selected */}
              {selectedPackIds.length > 0 && (
                <div className="sticky bottom-4 z-30 mt-8">
                  <button
                    onClick={handleNext}
                    className="flex w-full items-center justify-between rounded-2xl bg-green-600 px-6 py-4 text-white shadow-xl shadow-green-600/30 transition-all hover:bg-green-700 active:scale-[0.99]"
                  >
                    <div className="text-left">
                      <span className="block text-sm font-medium text-green-100">
                        {selectedPackIds.length} {selectedPackIds.length === 1 ? 'selección' : 'selecciones'}
                      </span>
                      <span className="text-xl font-bold">
                        ${totalGiftPrice.toLocaleString('es-AR')}
                      </span>
                    </div>
                    <span className="flex items-center gap-2 text-lg font-semibold">
                      Continuar
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                    </span>
                  </button>
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {/* ══════════ WIZARD STEPS (contained) ══════════ */}
      {step !== 'intro' && (
      <div className="max-w-2xl mx-auto px-4 py-8">

        {/* Step: Recipient */}
        {step === 'recipient' && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              ¿A quién va dirigido?
            </h2>
            <p className="text-gray-500 mb-6">
              Datos de la persona que va a recibir el regalo.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del homenajeado
                </label>
                <input
                  type="text"
                  value={recipientName}
                  onChange={e => setRecipientName(e.target.value)}
                  placeholder="Ej: Juan"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono (WhatsApp)
                </label>
                <input
                  type="tel"
                  value={recipientPhone}
                  onChange={e => setRecipientPhone(e.target.value)}
                  placeholder="Ej: 351 1234567"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Le vamos a avisar por WhatsApp cuando el regalo esté en camino.
                </p>
              </div>

              <div className="pt-2">
                <AddressTypeSelector value={addressType} onChange={handleTypeChange} />

                {isCountry ? (
                  <>
                    <NeighborhoodSelect
                      value={neighborhood}
                      onChange={setNeighborhood}
                      onSelect={handleNeighborhoodSelect}
                      selectedNeighborhood={geocodedAddress}
                      suggestions={neighborhoodSuggestions}
                      isLoading={isGeocodingLoading}
                      error={geocodingError}
                    />
                    {geocodedAddress && (
                      <div className="mt-4 flex gap-4 max-sm:flex-col">
                        <div className="flex-1">
                          <label className="mb-2 block text-[15px] font-semibold text-gray-800">Manzana</label>
                          <input
                            type="text"
                            value={block}
                            onChange={e => setBlock(e.target.value)}
                            placeholder="Ej: 14"
                            className="w-full rounded-lg border-2 border-gray-300 px-4 py-3 text-base outline-none transition-colors focus:border-green-500 focus:ring-2 focus:ring-green-200"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="mb-2 block text-[15px] font-semibold text-gray-800">Lote / Casa</label>
                          <input
                            type="text"
                            value={house}
                            onChange={e => setHouse(e.target.value)}
                            placeholder="Ej: 7"
                            className="w-full rounded-lg border-2 border-gray-300 px-4 py-3 text-base outline-none transition-colors focus:border-green-500 focus:ring-2 focus:ring-green-200"
                          />
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <StreetInput
                    value={streetAddress}
                    onChange={setStreetAddress}
                    onAddressSelect={handleStreetAddressSelect}
                    selectedAddress={geocodedAddress}
                    suggestions={streetSuggestions}
                    isLoading={isGeocodingLoading}
                    error={geocodingError}
                  />
                )}

                {!geocodedAddress && (
                  <button
                    type="button"
                    onClick={handleUseGps}
                    disabled={isGpsLoading}
                    className="mt-3 inline-flex items-center gap-2 text-sm text-green-700 hover:text-green-800 font-medium disabled:opacity-50"
                  >
                    {isGpsLoading ? (
                      <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    )}
                    {isGpsLoading ? 'Buscando...' : 'Usar mi ubicación'}
                  </button>
                )}
                {gpsError && <p className="mt-1 text-xs text-red-500">{gpsError}</p>}

                {!isCountry && (
                  <div>
                    <label className="mb-2 block text-[15px] font-semibold text-gray-800">
                      Piso / Departamento <span className="ml-2 text-sm font-normal text-gray-400">(opcional)</span>
                    </label>
                    <input
                      type="text"
                      value={suite}
                      onChange={e => setSuite(e.target.value)}
                      placeholder="Ej: 3ro B, Torre 2 Depto 45"
                      className="w-full rounded-lg border-2 border-gray-300 px-4 py-3 text-base outline-none transition-colors focus:border-green-500 focus:ring-2 focus:ring-green-200"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Step: From */}
        {step === 'from' && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              ¿De parte de quién?
            </h2>
            <p className="text-gray-500 mb-6">
              Así va a aparecer en la notificación al homenajeado.
            </p>

            <div className="flex flex-wrap gap-2 mb-4">
              {fromSuggestions.map(suggestion => (
                <button
                  key={suggestion}
                  onClick={() => setFromName(suggestion)}
                  className={`px-4 py-2 rounded-full text-sm transition-colors ${
                    fromName === suggestion
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {suggestion}
                </button>
              ))}
            </div>

            <input
              type="text"
              value={fromName}
              onChange={e => setFromName(e.target.value)}
              placeholder="Escribí tu nombre o apodo"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
        )}

        {/* Step: Message */}
        {step === 'message' && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Mensaje personal
            </h2>
            <p className="text-gray-500 mb-6">
              Escribí un mensaje para {recipientName}. Es opcional pero siempre alegra.
            </p>

            {specialDates.length > 0 && (
              <div className="mb-4">
                {specialDates.map(d => (
                  <span key={d.name} className="inline-block px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium mr-2">
                    {d.name}
                  </span>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-2 mb-4">
              {messageSuggestions.slice(0, 6).map(suggestion => (
                <button
                  key={suggestion}
                  onClick={() => setMessage(suggestion)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                    message === suggestion
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {suggestion}
                </button>
              ))}
            </div>

            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Escribí tu mensaje..."
              rows={4}
              maxLength={500}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-none"
            />
            <p className="text-xs text-gray-400 text-right mt-1">{message.length}/500</p>
          </div>
        )}

        {/* Step: Delivery date */}
        {step === 'delivery' && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              ¿Cuándo lo entregamos?
            </h2>
            <p className="text-gray-500 mb-6">
              Elegí el día y horario de entrega.
            </p>

            <div className="space-y-2">
              {deliveryDates.map(date => (
                <button
                  key={date.value}
                  onClick={() => setSelectedDate(date.value)}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                    selectedDate === date.value
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="font-medium">{date.label}</span>
                </button>
              ))}
            </div>

            {deliveryDates.length === 0 && (
              <p className="text-center text-gray-400 py-8">
                No hay fechas disponibles. Intentá con otra dirección.
              </p>
            )}
          </div>
        )}

        {/* Step: Preview */}
        {step === 'preview' && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Todo listo!
            </h2>
            <p className="text-gray-500 mb-6">
              Así va a ser la notificación que reciba {recipientName}.
            </p>

            {/* WhatsApp bubble preview */}
            <div className="bg-[#e5ddd5] rounded-2xl p-4 mb-6">
              <div className="bg-white rounded-lg p-4 max-w-sm mx-auto shadow-sm">
                <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">
                  Hola {recipientName},{'\n\n'}
                  Alguien te quiere mucho y tiene algo especial para vos! 🎁{'\n\n'}
                  Somos de Abasto Virtual, la tienda de frutas y verduras frescas de Córdoba.{'\n\n'}
                  <strong>{fromName}</strong> te preparó un regalo increíble que te vamos a entregar en{' '}
                  <strong>{geocodedAddress ? getDisplayName(geocodedAddress, { block, house, suite, isCountry }).split(',')[0] : ''}</strong>{' '}
                  el día <strong>{selectedDateLabel}</strong>.
                  {message && (
                    <>
                      {'\n\n'}💌 <em>{message}</em>
                    </>
                  )}
                  {'\n\n'}Si querés cambiar la fecha de entrega, avisanos por este mismo chat.
                  {'\n\n'}Que lo disfrutes! 🍓🥑
                </p>
                <p className="text-xs text-gray-400 text-right mt-2">Abasto Virtual</p>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Para</span>
                <span className="font-medium">{recipientName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">De parte de</span>
                <span className="font-medium">{fromName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Entrega</span>
                <span className="font-medium">{selectedDateLabel}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Selecciones</span>
                <span className="font-medium">{selectedPackIds.length}</span>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Navigation buttons */}
        <div className="mt-8 flex gap-3">
          {currentStepIndex > 0 && (
            <button
              onClick={goBack}
              className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Atrás
            </button>
          )}

          {step !== 'preview' ? (
            <button
              onClick={handleNext}
              disabled={isLoading}
              className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Verificando...' : 'Siguiente'}
            </button>
          ) : (
            <button
              onClick={handleConfirm}
              disabled={isLoading}
              className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Preparando...' : 'Confirmar y pagar'}
            </button>
          )}
        </div>
      </div>
      )}
    </MainLayout>
  );
}
